import { useIntl } from 'react-intl';
import _ from 'lodash';
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useFormik } from 'formik'
import { deleteOne, patchOne } from '../../../../services/common_requests';
import { toast } from 'react-toastify'
import { useEffect, useRef, useState } from 'react';
import { ID, KTSVG, QUERIES } from '../../../../../_dashboard/helpers';
import { useQueryClient } from 'react-query';
import { images } from '../../../hotel-management/hotel-details/core/_models';
import ImageEditModal from '../../../hotel-management/hotel-details/image-edit-modal/ImageEditModal';
import { AddAttributeModal } from './AddAttributeModal';
import { DeleteResponse } from '../../../hotel-management/hotel-details/HotelDetails';
import { ToggleButton } from './ToggleButton';
import { useAuth } from '../../../../modules/auth';

export interface Translation {
    _id: string;
    language: string;
    title?: string;
    subtitle?: string;
    content?: string;
    value?: string;
    label?:string;
}

export interface Attribute {
    _id: string;
    type: string;
    translations: Translation[];
    image?: {
        _id:string,
        url: string;
        seoUrl: string;
    };
    buttonLink?: string; 
}

interface Block {
    _id: string;
    position?: string;
    translations: Translation[];
    attributes: Attribute[];
    pageId:string
}
interface Image {
    _id: string;
    url: string;
    seoUrl?: string;
}

const BlockComponent = ({ block, pageId, selectedLanguage, handleAttributeAdded, handleAttributeDeleted }: { 
    block: any; 
    pageId: string; 
    handleAttributeAdded: (updatedBlockId: string, newAttribute: Attribute) => void;
    handleAttributeDeleted: (updatedBlockId: string, deletedAttributeId: string) => void;
    selectedLanguage: string 
}) => {
    const intl = useIntl()
    useEffect(() => {
        formik.setValues({
            ...block,
            attributes: [...block.attributes] 
        });
    }, [block.attributes]);
    const initialValues = {
        ...block,
        attributes: block.attributes.map((attr: any) => ({
            ...attr,
            translations: attr.translations || []
        }))
    };
    const [images, setImages] = useState<Image[]>([]);
    const [selectedImage, setSelectedImage] = useState<Image | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false)
    const handleImageClick = (img: Image) => {
        if (!img._id) {
            console.error("L'image cliquée n'a pas d'_id", img);
            return;
        }
        setSelectedImage(img);
        setIsModalOpen(true);
    };
    
    
    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedImage(null)
    }
    const { auth } = useAuth();
    const isSuperAdmin = auth?.role === 'ROLE_SUPER_ADMIN';
    const handleUpdateImage = async (updatedImage: { url: string; seoUrl?: string }) => {
        if (!selectedImage || !selectedImage._id) {
            console.error("selectedImage ou son _id est undefined", selectedImage);
            return;
        }
    
    
        if (!selectedImage._id) {
            console.error("selectedImage._id est undefined");
            return;
        }
    
        const formData = new FormData();
        if (updatedImage.url !== selectedImage.url) {
            const response = await fetch(updatedImage.url);
            const blob = await response.blob();
            const file = new File([blob], 'updated-image.jpg', { type: blob.type });
            formData.append('image', file);
        }
        if (updatedImage.seoUrl) {
            formData.append('seoUrl', updatedImage.seoUrl);
        }
    
        try {
            await patchOne(formData, `/images/${selectedImage._id}`).then((response) => {
                if (response?.payload?.code === 2021) {
                    queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
    
                    const updatedAttributes = formik.values.attributes.map((attr: Attribute) =>
                        attr.image?.url === selectedImage.url
                            ? { ...attr, image: { ...attr.image, ...updatedImage } }
                            : attr
                    );
    
                    formik.setFieldValue('attributes', updatedAttributes);
                    handleCloseModal();
                    toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.BLOCKS_UPDATED_SUCCESSFULLY' }), {
                        position: 'top-right',
                        autoClose: 4000,
                    });
                } else {
                    toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.BLOCKS_UPDATED' }), {
                        position: 'top-right',
                        autoClose: 4000,
                    });
                }
            });
        } catch (error) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.BLOCKS_UPDATED' }), {
                position: 'top-right',
                autoClose: 4000,
            });
        }
    };
    
    const [showAddAttributeModal, setShowAddAttributeModal] = useState(false)
    const queryClient = useQueryClient()
    const handleAddAttribute = (newAttribute: Omit<Attribute, '_id'> & { blockId: string }) => {
        if (newAttribute.blockId !== block._id) return;
        const finalAttribute: Attribute = {
            _id: Date.now().toString(),
            ...newAttribute,
            image: newAttribute.type === 'image' ? { 
                _id: newAttribute.image?._id || '',
                url: newAttribute.image?.url || '',
                seoUrl: newAttribute.image?.seoUrl || '' 
            } : undefined,
            //translations: newAttribute.translations
        };
        formik.setFieldValue('attributes', [...formik.values.attributes, finalAttribute]);

        queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
        handleAttributeAdded(block._id, finalAttribute);
    };
    

    const initialValuesRef = useRef(initialValues);
    const handleDeleteAttribute = async (attributeId: string) => {
        try {
            if (!attributeId) {
                toast.error(intl.formatMessage({ id: 'ERROR.MISSING_ATTRIBUTE_ID' }));
                return;
            }

            if ((attributeId)) {
               const response= await deleteOne(attributeId as unknown as ID, '/attributes');
               if (response?.payload?.code === 2020) {
                queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
                
                const updatedAttributes = formik.values.attributes.filter(
                    (attr: Attribute) => attr._id.toString() !== attributeId.toString()
                );
                formik.setFieldValue('attributes', updatedAttributes);
                handleAttributeDeleted(block._id, attributeId);

                toast.success(
                    intl.formatMessage({
                      id: 'ADMIN.COMMON.SUCCESS.ATTRIBUTE_DELETED_SUCCESSFULLY',
                    }),
                    {
                      position: 'top-right',
                      autoClose: 4000,
                    }
                  )
               }

               else {
                toast.error(
                  intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.ATTRIBUTE_DELETED',
                  }),
                  {
                    position: 'top-right',
                    autoClose: 4000,
                  }
                )
              }            }

        } catch (error) {
            // Rollback en cas d'erreur
            formik.setFieldValue('attributes', formik.values.attributes);
            toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.DELETE_FAILED',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
        }
    };
    
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                // Comparer avec les valeurs initiales
                const changedValues: Partial<Block> = _.omitBy(values, (value, key) =>
                    _.isEqual(value, initialValuesRef.current[key as keyof Block])
                );

                // Comparer les attributs individuellement
                changedValues.attributes = values.attributes.filter(
                    (attr: Attribute, index: number) =>
                        !_.isEqual(attr, initialValuesRef.current.attributes[index])
                );

                if (_.isEmpty(changedValues)) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.NO_MODIFICATION_DETECTED',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                    return;
                }

                const formData = new FormData();
                if (changedValues.position) {
                    formData.append("blockData[position]", changedValues.position);
                }

                if (changedValues.translations) {
                    changedValues.translations.forEach((translation: Translation, index: number) => {
                        formData.append(`blockData[translations][${index}][_id]`, translation._id);
                        formData.append(`blockData[translations][${index}][language]`, translation.language);
                        formData.append(`blockData[translations][${index}][title]`, translation.title || "");
                        formData.append(`blockData[translations][${index}][subtitle]`, translation.subtitle || "");
                        formData.append(`blockData[translations][${index}][content]`, translation.content || "");
                    });
                }

                if (changedValues.attributes) {
                    changedValues.attributes.forEach((attribute: Attribute, index: number) => {
                        formData.append(`attributesData[${index}][_id]`, attribute._id);

                        formData.append(`attributesData[${index}][type]`, attribute.type);
                        if (attribute.type !== 'image' && attribute.translations) {

                        attribute.translations.forEach((translation: Translation, tIndex: number) => {
                            formData.append(`attributesData[${index}][translations][${tIndex}][_id]`, translation._id);
                            formData.append(
                                `attributesData[${index}][translations][${tIndex}][language]`,
                                translation.language
                            );
                            formData.append(
                                `attributesData[${index}][translations][${tIndex}][value]`,
                                translation.value || ""
                            );
                        });
                    }
                    if (attribute.type === 'button' && attribute.buttonLink) {
                        formData.append(`attributesData[${index}][buttonLink]`, attribute.buttonLink);
                    }
                    });
                }

                const response = await patchOne(formData, `/blocks/${values._id}/update-with-attributes`);

                if (response?.payload?.code === 2021) {
                    toast.success(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.SUCCESS.SECTION_UPDATED_SUCCESSFULLY',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                    queryClient.invalidateQueries([QUERIES.SECTION_LIST, values.pageId])

                }
                else if (response?.payload?.code === 4003) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.POSITION_ALREADY_EXIST',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
                else if (response?.payload?.code === 4008) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.INVALID_LANGUAGE_CODE',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
                else {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.SECTION_UPDATED',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
            } catch (error) {
                toast.error(
                    intl.formatMessage({
                        id: 'ADMIN.COMMON.ERROR.SECTION_UPDATED',
                    }),
                    {
                        position: 'top-right',
                        autoClose: 4000,
                    }
                )
            }
        },
    });
    useEffect(() => {
        formik.setValues({
            ...block,
            attributes: block.attributes.map((attr: any) => ({
                ...attr,
                translations: attr.translations || []
            }))
        });
    }, [block]); 
    
    const renderEditableAttribute = (attribute: any, index: number) => {
        switch (attribute.type) {
            case 'image':
                return (
                    <div className="col mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h4 className="m-0">
                                {attribute.translations[0]?.label ?? ''}
                            </h4>
                        </div>
                        <section className=''>
                            <div className=''>
                                {attribute.image?.url && (
                                    <div className='d-flex flex-row align-items-top gap-4'>
                                        <img
                                            src={
                                                attribute.image?.url.includes('/images/')
                                                  ? process.env.REACT_APP_API_URL_IMAGE + attribute.image?.url
                                                  : attribute.image?.url
                                            }
                                            alt={attribute.image?.seoUrl}
                                            className='border-image h-175px cursor-pointer'
                                            onClick={() => handleImageClick(attribute.image)}
                                        />
                                        <div className='d-flex flex-column gap-2 mt-2'>
                                            <span className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                                onClick={() => handleImageClick(attribute.image)}>
                                                <i className='bi bi-pencil-fill fs-7'></i>
                                            </span>
                                            {isSuperAdmin && (
                                            <span 
                                                className='btn btn-icon btn-sm btn-active-color-danger'
                                                onClick={() => handleDeleteAttribute(attribute._id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <i className='bi bi-x fs-2'></i>
                                            </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                        <ImageEditModal
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            selectedImage={selectedImage || { id:'',url: '', seoUrl: '' }}
                            onSave={handleUpdateImage}
                        />
                    </div>
                )

            case 'text':
                return (
                    <div className="col-md-4 mb-3 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attribute.translations[0]?.label ?? 'Untitled'}
                                </strong>
                            </label>
                            {isSuperAdmin && (
                            <span 
                                className='btn btn-icon btn-sm btn-active-color-danger'
                                onClick={() => handleDeleteAttribute(attribute._id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                            )}
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            name={`attributes[${index}].translations[0].value`}
                            value={formik.values.attributes[index].translations[0]?.value}
                            onChange={formik.handleChange}
                        />
                    </div>
                )

            case 'button':
                return (
                    <div className="mb-3 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attribute.translations[0]?.label ?? 'Untitled'}
                                </strong>
                            </label>
                            {isSuperAdmin && (
                            <span 
                                className='btn btn-icon btn-sm btn-active-color-danger'
                                onClick={() => handleDeleteAttribute(attribute._id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                            )}
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            name={`attributes[${index}].translations[0].value`}
                            value={formik.values.attributes[index].translations[0]?.value}
                            onChange={formik.handleChange}
                        />
                        <div className="mt-4">
                            <label className="form-label mb-4">{intl.formatMessage({ id: 'BLOCK.BUTTON_LINK' })}</label>
                            <input
                                type="text"
                                className="form-control"
                                name={`attributes[${index}].buttonLink`}
                                value={formik.values.attributes[index].buttonLink || ''}
                                onChange={formik.handleChange}
                                placeholder={intl.formatMessage({ id: 'BLOCK.ENTER_BUTTON_LINK' })}

                            />
                        </div>

                    </div>
                )

            case 'long-text':
                return (
                    <div className="mb-4 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attribute.translations[0]?.label ?? 'Untitled'}
                                </strong>
                            </label>
                            {isSuperAdmin && (
                            <span 
                                className='btn btn-icon btn-sm btn-active-color-danger'
                                onClick={() => handleDeleteAttribute(attribute._id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                            )}
                        </div>
                        <ReactQuill
                            theme="snow"
                            value={formik.values.attributes[index].translations[0]?.value || ''}
                            onChange={(value) =>
                                formik.setFieldValue(`attributes[${index}].translations[0].value`, value)
                            }
                            className="bg-white"
                        />
                    </div>
                )

            default:
                return (
                    <div className="text-danger">
                        Type d'attribut non supporté : {attribute.type}
                    </div>
                )
        }
        
    }

    const handleToggleChange = async (id: string,newIsActive: boolean) => {
        try {
            const payload={
                'isActive':newIsActive
            }
           const response= await patchOne(payload, `/blocks/${block._id}`);
           if (response?.payload?.code === 2021) {
            toast.success(
                intl.formatMessage({
                    id: 'ADMIN.COMMON.SUCCESS.STATUS_UPDATED_SUCCESSFULLY',
                }),
                {
                    position: 'top-right',
                    autoClose: 4000,
                }
            )
            formik.setFieldValue('isActive', newIsActive);


        }
        } catch (error) {
            toast.error(
                intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.STATUS_UPDATED',
                }),
                {
                    position: 'top-right',
                    autoClose: 4000,
                }
            )
            formik.setFieldValue('isActive', !newIsActive);
        }
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="card mb-6">
                    <div className="">

                        <h2 className='my-2'>{block.translations[0]?.title}</h2>
                        <div className='my-3'>
                        <ToggleButton 
                            id={block._id}
                            isActive={formik.values.isActive} 
                            onChange={handleToggleChange} 
                        />
                        </div>
                        <label className="form-label">{intl.formatMessage({ id: 'BLOCK.TITLE' })}</label>
                        <input
                            type="text"
                            className="form-control mb-4"
                            name="translations[0].title"
                            value={formik.values.translations[0]?.title || ''}
                            onChange={formik.handleChange}
                            placeholder={intl.formatMessage({ id: 'BLOCK.TITLE' })}
                        />
                    {/*
                    <div className="mb-4">
                            <label className="form-label">{intl.formatMessage({ id: 'BLOCK.POSITION' })}</label>
                            <input
                                type="number"
                                className="form-control"
                                name="position"
                                value={formik.values.position}
                                onChange={formik.handleChange}
                                min="1"
                            />
                        </div> */} 
                        <div className="row">
                            {formik.values.attributes.map((attribute: any, index: number) => (
                                attribute.type === 'image' ? renderEditableAttribute(attribute, index) : null
                            ))}
                        </div>
                        <div className="row">
                            {formik.values.attributes.map((attribute: any, index: number) => (
                                attribute.type === 'text' ? renderEditableAttribute(attribute, index) : null
                            ))}
                        </div>
                        {formik.values.attributes.map((attribute: any, index: number) => (
                            attribute.type !== 'image' && attribute.type !== 'text' ? renderEditableAttribute(attribute, index) : null
                        ))}
                        
                        <button
                            type="button"
                            className="btn btn-border mt-3"
                            onClick={() => setShowAddAttributeModal(true)}
                            disabled={auth?.role !== 'ROLE_SUPER_ADMIN'}
                        >
                            <KTSVG path='/media/icons/duotune/arrows/addicon.svg' className='svg-icon-2' />
                            {intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.ADD' })}
                        </button>
                        <AddAttributeModal
                            show={showAddAttributeModal}
                            onHide={() => setShowAddAttributeModal(false)}
                            onAdd={handleAddAttribute}
                            blockId={block._id}
                            pageId={pageId}
                            initialValues={{ 
                                type: 'text', 
                                translations: [{
                                    _id:"1",
                                    language: 'fr',
                                    value: '',
                                    label:''
                                  
                                }] 
                            }}
                        />
                        <div className='modal-footer'>
                            <button
                                type="submit"
                                className="btn btn-primary me-2"
                                disabled={formik.isSubmitting}
                            >
                                {intl.formatMessage({
                                    id: 'HEADER.SAVE',
                                })}
                            </button>

                        </div>
                        <div className='separator mt-4'></div>
                    </div>
                </div>
            </form>

        </>


    )
}

export { BlockComponent };